import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import type { ChannelConfig, ChannelType, ChannelStatus } from '../../types/messaging.js';
import { CHANNEL_META } from '../../types/messaging.js';
import { wsClient } from '../../services/websocket-client.js';
import * as messagingApi from '../../services/messaging-api.js';
import { listCompanies } from '../../services/company-api.js';
import { getAgents } from '../../services/agent-api.js';
import './cp-channel-card.js';

interface WebSocketMessage<T = unknown> {
  type: string;
  payload: T;
  timestamp: string;
}

interface ChannelStatusPayload {
  channelType: ChannelType;
  channelConfigId?: number;
  error?: string;
}

interface QrCodePayload {
  channelType: ChannelType;
  qrCode: string;
}

interface CompanyOption {
  id: number;
  name: string;
}

interface AgentOption {
  id: number;
  name: string;
  color: string;
}

@customElement('cp-channel-settings')
export class CpChannelSettings extends LitElement {
  createRenderRoot() { return this; }

  @state() private _loading = true;
  @state() private _error = '';
  @state() private _channels: ChannelConfig[] = [];
  @state() private _companies: CompanyOption[] = [];
  @state() private _agents: AgentOption[] = [];
  @state() private _showAddForm = false;
  @state() private _addChannelType: ChannelType = 'telegram';
  @state() private _addCompanyId: number | null = null;
  @state() private _addAgentId: number | null = null;
  @state() private _addCredentials = '';
  @state() private _addDisplayName = '';
  @state() private _addSaving = false;
  @state() private _addError = '';

  private _boundWsHandler = this._onWsMessage.bind(this);

  connectedCallback(): void {
    super.connectedCallback();
    this._loadChannels();
    this._loadCompanies();
    this._loadAgents();
    wsClient.addEventListener('ws-message', this._boundWsHandler);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    wsClient.removeEventListener('ws-message', this._boundWsHandler);
  }

  private async _loadChannels(): Promise<void> {
    this._loading = true;
    this._error = '';
    try {
      this._channels = await messagingApi.getChannels();
    } catch (err) {
      this._error = err instanceof Error ? err.message : 'Fehler beim Laden der Channels';
    } finally {
      this._loading = false;
    }
  }

  private async _loadCompanies(): Promise<void> {
    try {
      const companies = await listCompanies('active');
      this._companies = companies.map((c) => ({ id: c.id, name: c.name }));
    } catch {
      // Non-critical, company dropdown will just be empty
    }
  }

  private async _loadAgents(): Promise<void> {
    try {
      const agents = await getAgents();
      // Filter out main agent (is_main = 1) — only show personal agents
      this._agents = agents
        .filter((a) => !a.is_main)
        .map((a) => ({ id: a.id, name: a.name, color: a.color }));
    } catch {
      // Non-critical, agent dropdown will just be empty
    }
  }

  private _onWsMessage(e: Event): void {
    const { detail } = e as CustomEvent<WebSocketMessage>;

    switch (detail.type) {
      case 'messaging:channel-connected': {
        const payload = detail.payload as ChannelStatusPayload;
        this._updateChannelStatus(payload.channelConfigId, payload.channelType, 'connected');
        break;
      }
      case 'messaging:channel-disconnected': {
        const payload = detail.payload as ChannelStatusPayload;
        this._updateChannelStatus(payload.channelConfigId, payload.channelType, 'disconnected');
        break;
      }
      case 'messaging:channel-error': {
        const payload = detail.payload as ChannelStatusPayload;
        this._updateChannelStatus(payload.channelConfigId, payload.channelType, 'error', payload.error);
        break;
      }
      case 'messaging:qr-code': {
        const payload = detail.payload as QrCodePayload;
        const card = this.querySelector(`cp-channel-card[data-type="${payload.channelType}"]`) as
          import('./cp-channel-card.js').CpChannelCard | null;
        if (card) {
          card.setQrCode(payload.qrCode);
        }
        break;
      }
    }
  }

  private _updateChannelStatus(channelConfigId: number | undefined, channelType: ChannelType, status: ChannelStatus, errorMessage?: string): void {
    this._channels = this._channels.map((ch) => {
      if (channelConfigId != null && ch.id === channelConfigId) {
        return { ...ch, status, error_message: errorMessage ?? ch.error_message };
      }
      if (channelConfigId == null && ch.channel_type === channelType) {
        return { ...ch, status, error_message: errorMessage ?? ch.error_message };
      }
      return ch;
    });
  }

  private _onConfigChanged(e: CustomEvent<{ channelType: ChannelType; config: ChannelConfig | null }>): void {
    const { config } = e.detail;
    if (config) {
      const idx = this._channels.findIndex((ch) => ch.id === config.id);
      if (idx >= 0) {
        this._channels = [...this._channels.slice(0, idx), config, ...this._channels.slice(idx + 1)];
      } else {
        this._channels = [...this._channels, config];
      }
    } else {
      // Config deleted - reload to get fresh state
      void this._loadChannels();
    }
  }

  private _countConnected(): number {
    return this._channels.filter((ch) => ch.status === 'connected').length;
  }

  private _getCompanyName(companyId: number | null): string | null {
    if (companyId == null) return null;
    return this._companies.find((c) => c.id === companyId)?.name ?? null;
  }

  private _getAgentName(agentId: number | null): string | null {
    if (agentId == null) return null;
    return this._agents.find((a) => a.id === agentId)?.name ?? null;
  }

  private _toggleAddForm(): void {
    this._showAddForm = !this._showAddForm;
    this._addError = '';
    this._addCredentials = '';
    this._addDisplayName = '';
    this._addCompanyId = null;
    this._addAgentId = null;
    this._addChannelType = 'telegram';
  }

  private async _addChannel(): Promise<void> {
    if (this._addSaving) return;
    this._addSaving = true;
    this._addError = '';

    const meta = CHANNEL_META[this._addChannelType];
    if (!this._addCredentials.trim()) {
      this._addError = `${meta.credentialLabel} ist erforderlich`;
      this._addSaving = false;
      return;
    }

    try {
      const created = await messagingApi.createChannel({
        channel_type: this._addChannelType,
        display_name: this._addDisplayName.trim() || meta.label,
        credentials: this._addCredentials.trim(),
        company_id: this._addCompanyId ?? undefined,
        agent_id: this._addAgentId ?? undefined,
      });
      this._channels = [...this._channels, created];
      this._showAddForm = false;
      this._addCredentials = '';
      this._addDisplayName = '';
      this._addCompanyId = null;
      this._addAgentId = null;
    } catch (err) {
      this._addError = err instanceof Error ? err.message : 'Fehler beim Erstellen';
    } finally {
      this._addSaving = false;
    }
  }

  private _renderAddForm() {
    if (!this._showAddForm) return nothing;

    const meta = CHANNEL_META[this._addChannelType];

    return html`
      <div class="cp-channel-settings__add-form">
        ${this._addError ? html`<div class="cp-channel-settings__error">${this._addError}</div>` : nothing}

        <div class="cp-channel-settings__field">
          <label class="cp-channel-settings__label">Typ</label>
          <select
            class="cp-channel-settings__select"
            .value=${this._addChannelType}
            @change=${(e: Event) => { this._addChannelType = (e.target as HTMLSelectElement).value as ChannelType; }}
          >
            <option value="telegram">Telegram</option>
            <option value="slack">Slack</option>
            <option value="whatsapp">WhatsApp</option>
          </select>
        </div>

        <div class="cp-channel-settings__field">
          <label class="cp-channel-settings__label">Zuordnung</label>
          <select
            class="cp-channel-settings__select"
            @change=${(e: Event) => {
              const val = (e.target as HTMLSelectElement).value;
              this._addCompanyId = null;
              this._addAgentId = null;
              if (val.startsWith('company:')) {
                this._addCompanyId = Number(val.slice(8));
              } else if (val.startsWith('agent:')) {
                this._addAgentId = Number(val.slice(6));
              }
            }}
          >
            <option value="">Generischer Agent</option>
            ${this._companies.length > 0 ? html`
              <optgroup label="Firmen">
                ${this._companies.map((c) => html`
                  <option value=${'company:' + c.id}>${c.name}</option>
                `)}
              </optgroup>
            ` : nothing}
            ${this._agents.length > 0 ? html`
              <optgroup label="Persoenliche Agenten">
                ${this._agents.map((a) => html`
                  <option value=${'agent:' + a.id}>${a.name}</option>
                `)}
              </optgroup>
            ` : nothing}
          </select>
        </div>

        <div class="cp-channel-settings__field">
          <label class="cp-channel-settings__label">Anzeigename</label>
          <input
            class="cp-channel-settings__input"
            type="text"
            placeholder=${meta.label}
            .value=${this._addDisplayName}
            @input=${(e: Event) => { this._addDisplayName = (e.target as HTMLInputElement).value; }}
          />
        </div>

        <div class="cp-channel-settings__field">
          <label class="cp-channel-settings__label">${meta.credentialLabel}</label>
          <input
            class="cp-channel-settings__input cp-channel-settings__input--mono"
            type="password"
            placeholder=${meta.credentialPlaceholder}
            .value=${this._addCredentials}
            @input=${(e: Event) => { this._addCredentials = (e.target as HTMLInputElement).value; }}
          />
          <span class="cp-channel-settings__hint">${meta.credentialHint}</span>
        </div>

        <div class="cp-channel-settings__actions">
          <button
            class="cp-channel-settings__btn cp-channel-settings__btn--save"
            @click=${this._addChannel}
            ?disabled=${this._addSaving}
          >${this._addSaving ? 'Erstellt...' : 'Channel erstellen'}</button>
          <button
            class="cp-channel-settings__btn cp-channel-settings__btn--cancel"
            @click=${this._toggleAddForm}
          >Abbrechen</button>
        </div>
      </div>
    `;
  }

  render() {
    const connectedCount = this._countConnected();

    return html`
      <div class="cp-channel-settings">
        <div class="cp-channel-settings__header">
          <h3 class="cp-channel-settings__title">Messaging Channels</h3>
          ${connectedCount > 0 ? html`
            <span class="cp-channel-settings__status cp-channel-settings__status--active">
              ${connectedCount} verbunden
            </span>
          ` : html`
            <span class="cp-channel-settings__status cp-channel-settings__status--inactive">
              Nicht konfiguriert
            </span>
          `}
        </div>

        ${this._error ? html`
          <div class="cp-channel-settings__error">${this._error}</div>
        ` : nothing}

        ${this._loading ? html`
          <p class="cp-channel-settings__loading">Channels werden geladen...</p>
        ` : html`
          <div class="cp-channel-settings__list" @config-changed=${this._onConfigChanged}>
            ${this._channels.length === 0 && !this._showAddForm ? html`
              <p class="cp-channel-settings__empty">Noch keine Channels konfiguriert.</p>
            ` : nothing}
            ${this._channels.map((ch) => {
              const companyName = this._getCompanyName(ch.company_id);
              const agentName = this._getAgentName(ch.agent_id);
              return html`
                <cp-channel-card
                  data-type=${ch.channel_type}
                  data-id=${ch.id}
                  .meta=${CHANNEL_META[ch.channel_type]}
                  .config=${ch}
                  .companyLabel=${companyName}
                  .agentLabel=${agentName}
                ></cp-channel-card>
              `;
            })}
          </div>

          ${this._renderAddForm()}

          ${!this._showAddForm ? html`
            <button
              class="cp-channel-settings__btn cp-channel-settings__btn--add"
              @click=${this._toggleAddForm}
            >+ Channel hinzufuegen</button>
          ` : nothing}
        `}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-channel-settings': CpChannelSettings;
  }
}
